import type { ConsumerVerdict } from './audience';
import type { RowTone } from './salesReport';

/**
 * Every string the consumer framing of the locked report shows (#2046), in
 * one module so __tests__/consumerCopy.test.ts can hold it to the copy rules.
 * The free side states pass/fail only: company names, records and
 * screenshots appear only in UNLOCK_ITEMS, as what the unlock adds.
 */

export const CONSENT_HEADLINE: Record<ConsumerVerdict['consent'], string> = {
  leaked: 'After you said no, this site kept loading advertising trackers.',
  stopped: 'This site stopped its advertising trackers when you said no.',
  unknown: "We couldn't confirm what this site does when you say no.",
};

export const GPC_LINE: Record<ConsumerVerdict['gpc'], string> = {
  ignored: 'It also ignored Global Privacy Control, the opt-out signal your browser can send.',
  honored: 'It honored Global Privacy Control, the opt-out signal your browser can send.',
  unknown: "We couldn't confirm whether it honors Global Privacy Control, the opt-out signal your browser can send.",
};

export const CONSUMER_SUB =
  'We visited the site in a real browser, said no to tracking, and watched what loaded next.';

export interface ConsumerRow {
  label: string;
  badge: string;
  tone: RowTone;
}

export const CONSENT_ROW: Record<ConsumerVerdict['consent'], ConsumerRow> = {
  leaked: { label: 'Stops trackers after you say no', badge: 'KEPT LOADING', tone: 'fail' },
  stopped: { label: 'Stops trackers after you say no', badge: 'STOPPED', tone: 'pass' },
  unknown: { label: 'Stops trackers after you say no', badge: 'NOT CONFIRMED', tone: 'neutral' },
};

export const GPC_ROW: Record<ConsumerVerdict['gpc'], ConsumerRow> = {
  ignored: { label: "Honors your browser's opt-out signal", badge: 'IGNORED', tone: 'fail' },
  honored: { label: "Honors your browser's opt-out signal", badge: 'HONORED', tone: 'pass' },
  unknown: { label: "Honors your browser's opt-out signal", badge: 'NOT CONFIRMED', tone: 'neutral' },
};

export const OTHER_CHECKS_SUMMARY = 'Everything else we checked';

export const UNLOCK_ITEMS: { title: string; body: string }[] = [
  {
    title: 'Every company named',
    body: 'The name of each advertising and analytics company that loaded on this site.',
  },
  {
    title: 'Which ones kept loading after you said no',
    body: 'Each company that kept receiving data after the opt-out and after the Global Privacy Control signal.',
  },
  {
    title: 'Network records and screenshots',
    body: 'The requests we captured and screenshots of the pages the scan visited.',
  },
  {
    title: 'The California rule for each check',
    body: 'The California privacy rule behind every check we ran.',
  },
];

/**
 * The unlock box's description in consumer mode, in place of the owner copy
 * ("… and how to fix it"). It describes what the unlock adds, so it may name
 * companies, like UNLOCK_ITEMS.
 */
export const CONSUMER_UNLOCK_DESC =
  'Unlock the report to see every company that loaded on this site and which ones kept loading after you said no.';

export const CONSUMER_FINE_PRINT =
  'The free check works on any site, with no account or card. The results are free, and the company names unlock after.';

/** Every string above, flattened, for the copy guard. */
export function allConsumerCopy(): string[] {
  const rows = [...Object.values(CONSENT_ROW), ...Object.values(GPC_ROW)];
  return [
    ...Object.values(CONSENT_HEADLINE),
    ...Object.values(GPC_LINE),
    CONSUMER_SUB,
    ...rows.flatMap((r) => [r.label, r.badge]),
    OTHER_CHECKS_SUMMARY,
    ...UNLOCK_ITEMS.flatMap((i) => [i.title, i.body]),
    CONSUMER_UNLOCK_DESC,
    CONSUMER_FINE_PRINT,
  ];
}

/** The strings shown for free, before any unlock. */
export function freeConsumerCopy(): string[] {
  const unlock = new Set(UNLOCK_ITEMS.flatMap((i) => [i.title, i.body]));
  return allConsumerCopy().filter((s) => !unlock.has(s) && s !== CONSUMER_UNLOCK_DESC && s !== CONSUMER_FINE_PRINT);
}
